import { Button, Typography } from '@/components/atoms';
import { ChevronDownIcon } from '@/components/atoms/icons';
import { TextHeader } from '@/components/molecules';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useTranslation, useUIColors } from '@/core/hooks';
import { logger } from '@/core/logger';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { convertRichTextToHTML, fetchLegalDocument, type LegalDocument } from '@/core/services/legalDocumentsService';
import { router } from 'expo-router';
import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Linking, Platform, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { WebView } from 'react-native-webview';

export interface LegalDocumentScreenProps {
    documentName: 'terms-and-conditions' | 'privacy-policy';
    document?: LegalDocument | null;
    onAccept?: () => void;
    from?: string;
}

export function LegalDocumentScreen({ 
    documentName, 
    document: propDocument,
    onAccept,
    from
}: LegalDocumentScreenProps) {
    const colors = useUIColors();
    const { t } = useTranslation();
    const { currentLanguage, isRTL } = useI18nContext();
    const insets = useSafeAreaInsets();
    const [document, setDocument] = useState<LegalDocument | null>(propDocument || null);
    const [isLoading, setIsLoading] = useState(!propDocument);
    const [isWebViewLoading, setIsWebViewLoading] = useState(false);
    const [htmlContent, setHtmlContent] = useState<string>('');
    const [hasAttemptedLoad, setHasAttemptedLoad] = useState(!!propDocument); // Track if we've attempted to load
    const [webViewHeight, setWebViewHeight] = useState<number>(400); // Initial height
    const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
    const scrollViewRef = useRef<ScrollView>(null);
    const webIframeRef = useRef<any>(null); // For web platform iframe height calculation

    const legalViewAction =
        documentName === 'terms-and-conditions'
            ? 'terms_and_conditions_viewed'
            : 'privacy_policy_viewed';
    useCatalogDynatraceView(DT_SCREEN.LEGAL, legalViewAction, Boolean(document) && !isLoading);

    // Fetch document - always refetch when language changes, even if propDocument was provided
    useEffect(() => {
        // If propDocument is provided and we haven't changed language, use it
        // But we should always fetch fresh content when language changes
        const loadDocument = async () => {
            try {
                setIsLoading(true);
                setHasAttemptedLoad(false); // Reset flag when starting new load
                const locale = currentLanguage === 'ar' ? 'ar' : 'en';
                const fetched = await fetchLegalDocument(documentName, locale);
                if (fetched) {
                    setDocument(fetched);
                } else {
                    logger.warn('LegalDocumentScreen', 'Document not found', { documentName, locale: currentLanguage });
                }
            } catch (error) {
                logger.error('LegalDocumentScreen', 'Failed to fetch document', {
                    documentName,
                    locale: currentLanguage,
                    error: error instanceof Error ? error.message : String(error),
                });
            } finally {
                setIsLoading(false);
                setHasAttemptedLoad(true); // Mark that we've attempted to load
            }
        };

        void loadDocument();
    }, [documentName, currentLanguage]); // Remove propDocument from dependencies - always fetch based on current language

    // Convert RichText to HTML when document changes
    useEffect(() => {
        if (document?.content) {
            const html = convertRichTextToHTML(document.content);
            setHtmlContent(html);
        } else {
            // Only clear htmlContent if we've attempted to load and document has no content
            if (hasAttemptedLoad) {
                setHtmlContent('');
            }
        }
    }, [document, hasAttemptedLoad]);

    // Update iframe height on web when content loads
    useEffect(() => {
        if (Platform.OS === 'web' && htmlContent && webIframeRef.current) {
            const updateHeight = () => {
                try {
                    const iframe = webIframeRef.current as any;
                    if (iframe && iframe.contentWindow && iframe.contentDocument) {
                        const doc = iframe.contentDocument;
                        const height = Math.max(
                            doc.body.scrollHeight,
                            doc.body.offsetHeight,
                            doc.documentElement.clientHeight,
                            doc.documentElement.scrollHeight,
                            doc.documentElement.offsetHeight
                        );
                        if (iframe.style) {
                            iframe.style.height = `${height}px`;
                        }
                    }
                } catch (error) {
                    // Cross-origin or other iframe access errors - ignore
                }
            };

            // Wait for iframe to load
            const timer = setTimeout(updateHeight, 500);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [htmlContent]);


    const handleBackPress = () => {
        router.back();
    };

    const handleSupportPress = () => {
        router.push('/service-request');
    };

    const handleAcceptPress = () => {
        // Call onAccept callback first (if provided)
        // This allows the parent to update state before navigation
        if (onAccept) {
            onAccept();
        } else {
            // If no callback, just navigate back
            router.back();
        }
    };

    const handleScroll = (event: any) => {
        const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
        const paddingToBottom = 100; // Threshold to consider "at bottom"
        const isAtBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - paddingToBottom;
        setIsScrolledToBottom(isAtBottom);
    };

    const getTitle = () => {
        if (documentName === 'terms-and-conditions') {
            return 'Terms & Conditions';
        }
        return 'Privacy Policy';
    };

    const handleShouldStartLoadWithRequest = (request: any) => {
        const url = request.url;
        
        // Allow same-origin navigation
        if (url.startsWith('data:') || url.startsWith('about:')) {
            return true;
        }

        // Handle external links (mailto:, tel:, http:, https:)
        const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url);
        
        if (hasScheme) {
            // Open external links using Linking
            Linking.openURL(url).catch((err) => {
                logger.error('LegalDocumentScreen', 'Failed to open link', { url, error: err });
            });
            return false; // Prevent WebView from loading external URLs
        }

        return false;
    };

    const handleLoadStart = () => {
        setIsWebViewLoading(true);
    };

    const handleLoadEnd = () => {
        setIsWebViewLoading(false);
        // Inject JavaScript to get content height and update WebView height
        if (htmlContent) {
            // Use setTimeout to ensure content is fully rendered
            setTimeout(() => {
                // This will be handled by onMessage
            }, 100);
        }
    };

    const handleMessage = (event: any) => {
        try {
            const data = JSON.parse(event.nativeEvent.data);
            if (data.type === 'contentHeight' && data.height) {
                setWebViewHeight(data.height);
            }
        } catch (error) {
            // Ignore parse errors
        }
    };

    const handleError = (syntheticEvent: any) => {
        const { nativeEvent } = syntheticEvent;
        logger.error('LegalDocumentScreen', 'WebView error', { error: nativeEvent });
        setIsWebViewLoading(false);
    };

    // Generate HTML with proper styling
    const generateHTML = () => {
        const backgroundColor = colors.backgroundLevel2;
        const textColor = colors.fontPrimary;
        const direction = isRTL ? 'rtl' : 'ltr';
        const textAlign = isRTL ? 'right' : 'left';

        return `
            <!DOCTYPE html>
            <html dir="${direction}" lang="${currentLanguage}">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
                <script>
                    function sendHeight() {
                        const height = Math.max(
                            document.body.scrollHeight,
                            document.body.offsetHeight,
                            document.documentElement.clientHeight,
                            document.documentElement.scrollHeight,
                            document.documentElement.offsetHeight
                        );
                        // Support both web and native platforms
                        if (window.ReactNativeWebView) {
                            window.ReactNativeWebView.postMessage(JSON.stringify({
                                type: 'contentHeight',
                                height: height
                            }));
                        } else if (window.parent && window.parent.postMessage) {
                            // Fallback for web iframe
                            window.parent.postMessage(JSON.stringify({
                                type: 'contentHeight',
                                height: height
                            }), '*');
                        }
                    }
                    window.addEventListener('load', sendHeight);
                    window.addEventListener('resize', sendHeight);
                    // Also send height after a short delay to ensure content is rendered
                    setTimeout(sendHeight, 100);
                    // Additional delay for web to ensure all content is loaded
                    setTimeout(sendHeight, 500);
                </script>
                <style>
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }
                    body {
                        font-family: -apple-system, BlinkMacSystemFont, 'Matter', 'Segoe UI', Roboto, sans-serif;
                        font-size: 16px;
                        line-height: 20px;
                        color: ${textColor};
                        background-color: ${backgroundColor};
                        padding: 16px;
                        direction: ${direction};
                        text-align: ${textAlign};
                    }
                    h1, h2, h3, h4, h5, h6 {
                        font-weight: 600;
                        margin-bottom: 8px;
                        margin-top: 16px;
                        font-size: 16px;
                        line-height: 20px;
                    }
                    h1:first-child, h2:first-child, h3:first-child, h4:first-child, h5:first-child, h6:first-child {
                        margin-top: 0;
                    }
                    p {
                        margin-bottom: 16px;
                        font-size: 16px;
                        line-height: 20px;
                    }
                    a {
                        color: ${textColor};
                        text-decoration: underline;
                    }
                    strong {
                        font-weight: 600;
                    }
                    em {
                        font-style: italic;
                    }
                    u {
                        text-decoration: underline;
                    }
                    code {
                        font-family: monospace;
                        background-color: ${colors.backgroundLevel3};
                        padding: 2px 4px;
                        border-radius: 4px;
                    }
                    ul, ol {
                        margin-left: ${isRTL ? '0' : '20px'};
                        margin-right: ${isRTL ? '20px' : '0'};
                        margin-bottom: 16px;
                    }
                    li {
                        margin-bottom: 8px;
                    }
                    blockquote {
                        border-left: ${isRTL ? 'none' : '4px solid ' + colors.outlineActive};
                        border-right: ${isRTL ? '4px solid ' + colors.outlineActive : 'none'};
                        padding-left: ${isRTL ? '0' : '16px'};
                        padding-right: ${isRTL ? '16px' : '0'};
                        margin: 16px 0;
                        font-style: italic;
                    }
                    hr {
                        border: none;
                        border-top: 1px solid ${colors.outlineInactive};
                        margin: 16px 0;
                    }
                </style>
            </head>
            <body>
                ${htmlContent}
            </body>
            </html>
        `;
    };

    const styles = StyleSheet.create({
        container: {
            flex: 1,
            backgroundColor: colors.backgroundLevel2,
        },
        scrollView: {
            flex: 1,
        },
        scrollContent: {
            flexGrow: 1,
        },
        webViewContainer: {
            backgroundColor: colors.backgroundLevel2,
        },
        loadingContainer: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: colors.backgroundLevel2,
        },
        buttonContainer: {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            paddingHorizontal: 16,
            paddingTop: 16,
            paddingBottom: Math.max(insets.bottom, 34),
            backgroundColor: colors.backgroundLevel1,
            borderTopWidth: 1,
            borderTopColor: colors.outlineInactive,
        },
        acceptButton: {
            backgroundColor: '#1a1a1a', // Black button as per Figma
            borderRadius: 4,
        },
        acceptButtonText: {
            color: '#ffffff', // White text as per Figma
        },
        scrollPromptContainer: {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            paddingTop: 12,
            paddingHorizontal: 16,
            paddingBottom:insets.bottom + 30,
            backgroundColor: colors.backgroundLevel1,
            borderTopWidth: 1,
            borderTopColor: colors.outlineInactive,
        },
        scrollPromptText: {
            fontSize: 16,
            fontWeight: '600',
            color: colors.buttonDisabled,
        },
    });

    return (
        <View style={styles.container}>
            <TextHeader
                title={getTitle()}
                onBackPress={handleBackPress}
                showSupportButton={true}
                onSupportPress={handleSupportPress}
            />

            {(isLoading || isWebViewLoading) && (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color={colors.fontPrimary} />
                </View>
            )}

            <ScrollView
                ref={scrollViewRef}
                style={styles.scrollView}
                contentContainerStyle={[
                    styles.scrollContent,
                    onAccept && from === 'payment-summary' && { paddingBottom: 120 }, // Space for fixed bottom container
                ]}
                showsVerticalScrollIndicator={true}
                onScroll={handleScroll}
                scrollEventThrottle={16}
            >
                {htmlContent ? (
                    Platform.OS === 'web' ? (
                        // Web: Render HTML directly using iframe (no WebView)
                        <View style={styles.webViewContainer}>
                            {/* @ts-ignore - Web-specific iframe element */}
                            <iframe
                                ref={webIframeRef}
                                srcDoc={generateHTML()}
                                style={{
                                    width: '100%',
                                    border: 'none',
                                    minHeight: '400px',
                                    overflow: 'hidden',
                                }}
                                onLoad={() => {
                                    // Update height after iframe loads
                                    setTimeout(() => {
                                        try {
                                            const iframe = webIframeRef.current as any;
                                            if (iframe && iframe.contentWindow && iframe.contentDocument) {
                                                const doc = iframe.contentDocument;
                                                const height = Math.max(
                                                    doc.body.scrollHeight,
                                                    doc.body.offsetHeight,
                                                    doc.documentElement.clientHeight,
                                                    doc.documentElement.scrollHeight,
                                                    doc.documentElement.offsetHeight
                                                );
                                                if (iframe.style) {
                                                    iframe.style.height = `${height}px`;
                                                }
                                            }
                                        } catch (error) {
                                            // Cross-origin or other iframe access errors - ignore
                                        }
                                    }, 100);
                                }}
                            />
                        </View>
                    ) : (
                        // Native: Use WebView
                        <WebView
                            source={{ html: generateHTML() }}
                            style={[styles.webViewContainer, { height: webViewHeight }]}
                            onShouldStartLoadWithRequest={handleShouldStartLoadWithRequest}
                            onLoadStart={handleLoadStart}
                            onLoadEnd={handleLoadEnd}
                            onError={handleError}
                            onMessage={handleMessage}
                            javaScriptEnabled={true}
                            domStorageEnabled={false}
                            cacheEnabled={false}
                            startInLoadingState={false}
                            scalesPageToFit={true}
                            showsVerticalScrollIndicator={false}
                            showsHorizontalScrollIndicator={false}
                            scrollEnabled={false}
                            nestedScrollEnabled={false}
                            originWhitelist={['*']}
                            mixedContentMode="compatibility"
                            userAgent={Platform.select({
                                ios: 'Mozilla/5.0 (iPhone; CPU iPhone OS 15_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/15.0 Mobile/15E148 Safari/604.1',
                                android: 'Mozilla/5.0 (Linux; Android 13; Mobile) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/119.0.0.0 Mobile Safari/537.36',
                                default: undefined as unknown as string,
                            })}
                        />
                    )
                ) : hasAttemptedLoad && !isLoading && !isWebViewLoading && !htmlContent && (
                    <View style={styles.loadingContainer}>
                        <Typography variant="body" color="primary">
                            {t('common.noContentAvailable')}
                        </Typography>
                    </View>
                )}
            </ScrollView>

            {/* Fixed bottom container for accept button and scroll prompt */}
            {onAccept && from === 'payment-summary' && !isLoading && !isWebViewLoading && (
                <>
                    {/* Show accept button only when scrolled to bottom */}
                    {isScrolledToBottom && (
                        <View style={styles.buttonContainer}>
                            <Button
                                title="Accept"
                                variant="primary"
                                fullWidth={true}
                                onPress={handleAcceptPress}
                                style={styles.acceptButton}
                                textStyle={styles.acceptButtonText}
                                theme="light" // Use light theme to get black button (#1a1a1a)
                            />
                        </View>
                    )}
                    
                    {/* Scroll prompt - hidden when scrolled to bottom */}
                    {!isScrolledToBottom && (
                        <View style={styles.scrollPromptContainer}>
                            <Typography variant="headline" color="secondary" style={styles.scrollPromptText}>
                                {t('common.scrollToBottomToAccept')}
                            </Typography>
                            <ChevronDownIcon size={18} color={colors.buttonDisabled} />
                        </View>
                    )}
                </>
            )}
        </View>
    );
}

